import { Badge, Button, Card, CardContent, LoadingState } from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { listInspections } from '../../../api/inspections.js';
import { CreateInspectionSheet } from '../inspections/create-inspection-sheet.js';

import type { UnitRow } from '../../../api/buildings.js';
import type { UnitDetailBuilding } from '../../../api/units.js';


interface Props {
  unit: UnitRow;
  building: UnitDetailBuilding;
}

const STATUS_TONE: Record<string, 'neutral' | 'warning' | 'success' | 'danger'> = {
  scheduled: 'neutral',
  in_progress: 'warning',
  submitted: 'success',
  cancelled: 'danger',
};

/**
 * Inspection runs scoped to this unit. Inspection templates target a
 * unit or a building, so we filter the unit's runs and let the user
 * launch a new one with the building/unit pre-filled.
 */
export function InspectionsTab({ unit, building: _building }: Props) {
  const navigate = useNavigate();
  const [openCreate, setOpenCreate] = useState(false);

  const inspectionsQuery = useQuery({
    queryKey: ['inspections', { unitId: unit.id }],
    queryFn: () => listInspections({ unitId: unit.id, limit: 25 }),
  });

  const rows = inspectionsQuery.data?.inspections ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Link
          to={`/inspections/runs?unitId=${unit.id}`}
          className="text-sm text-primary hover:underline"
        >
          View all inspections for this unit →
        </Link>
        <Button size="sm" onClick={() => setOpenCreate(true)}>
          <ClipboardCheck className="h-3.5 w-3.5" /> New inspection
        </Button>
      </div>
      {inspectionsQuery.isLoading ? (
        <LoadingState rows={3} />
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-fg-secondary">
            No inspections recorded for this unit yet.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y divide-border p-0">
            {rows.map((insp) => (
              <button
                key={insp.id}
                onClick={() => navigate(`/inspections/runs/${insp.id}`)}
                className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left text-sm hover:bg-surface-muted"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {insp.scheduledFor
                      ? `Scheduled ${new Date(insp.scheduledFor).toLocaleDateString()}`
                      : insp.submittedAt
                        ? `Submitted ${new Date(insp.submittedAt).toLocaleDateString()}`
                        : 'Unscheduled inspection'}
                  </div>
                  {insp.notes ? (
                    <div className="truncate text-xs text-fg-muted">{insp.notes}</div>
                  ) : null}
                </div>
                <Badge
                  variant={
                    (STATUS_TONE[insp.status] as 'success' | 'warning' | 'danger') ?? 'default'
                  }
                  className="capitalize"
                >
                  {insp.status.replace('_', ' ')}
                </Badge>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
      {openCreate ? (
        <CreateInspectionSheet
          onClose={() => setOpenCreate(false)}
          buildingId={unit.buildingId}
        />
      ) : null}
    </div>
  );
}
